import React, {
	useCallback,
	useMemo,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	Button,
	Form,
	Input,
	Label
} from 'semantic-ui-react';

import { log } from '../../services/utils';
import {
	downloadAsset,
	uploadAsset
} from '../../api/nora/assets';

import DDFileUploadModal from '../DDFileUpload';

const FormFileInput = ( {
	align = 'left',
	allowMultiple = false,
	download,
	errors = [],
	fieldLabel,
	fieldLabelAlignment = 'left',
	fieldLabelPosition = 'above',
	forceValidation,
	name,
	onBlur,
	onUpload,
	required,
	upload = false,
	value,
	width,
	...props
} ) => {
	const inputRef = useRef( null );

	const [ validate, setValidate ] = useState( false );
	const [ fileUploadModal, setFileUploadModal ] = useState( false );

	const isInvalid = useCallback(
		() => validate || forceValidation
			? errors.some( error => error.error )
			: false,
		[
			errors,
			forceValidation,
			validate
		]
	);

	const handleUploadClick = useCallback( () => {
		setFileUploadModal( true );
	}, [] );

	const handleDownloadClick = useCallback( () => {
		downloadAsset( download.uid );
	}, [ download ] );

	const handleBlur = useCallback( e => {
		// enable validation when field loses focus
		if ( errors.length && !validate ) {
			setValidate( true );
		}

		if ( onBlur ) {
			onBlur( e );
		}
	}, [
		errors.length,
		onBlur,
		validate
	] );

	const handleUpload = useCallback( response => {
		const target = {
			name,
			value: response
		};

		onUpload( { target } );
	}, [
		name,
		onUpload
	] );

	const handleCloseFileUploadModal = useCallback( () => {
		setFileUploadModal( false );
	}, [] );

	const alignment = useMemo(
		() => align === 'right'
			? { marginLeft: 'auto' }
			: null,
		[ align ]
	);

	return (
		<>{ log( `- ${ name } file input` ) }
			<Form.Field
				id={ name }
				error={ isInvalid() }
				required={ required }
				style={ alignment }
				width={ width }
			>
				{
					fieldLabel && fieldLabelPosition === 'above'
						? <label
							style={ { textAlign: fieldLabelAlignment } }
						>
							{ fieldLabel }
						</label>
						: null
				}
				<Input
					labelPosition="left"
					name={ name }
					onBlur={ handleBlur }
					onClick={ upload
						? handleUploadClick
						: handleDownloadClick
					}
					readOnly
					size="huge"
					transparent
					value={ !value
						? 'Please upload a file'
						: value
					}
					{ ...props }
					autoComplete="chrome-off"
					style={ { alignItems: 'center' } }
				>
					<Label
						basic
						icon={ upload
							? 'file'
							: 'download'
						}
						style={ {
							border: 'none',
							cursor: 'pointer',
							paddingRight: 0
						} }
					/>
					<input
						ref={ inputRef }
						style={ { cursor: 'pointer' } }
					/>
					{
						upload && value
							? <Button
								basic
								icon="download"
								onClick={ handleDownloadClick }
							/>
							: null
					}
				</Input>
				{
					fieldLabel && fieldLabelPosition === 'below'
						? <label
							style={ {
								fontSize: '0.85em',
								fontWeight: 300,
								lineHeight: '1.3',
								margin: '0.33333333rem 0em 1rem 0em',
								textAlign: fieldLabelAlignment
							} }
						>
							{ fieldLabel }
						</label>
						: null
				}
			</Form.Field>
			<DDFileUploadModal
				allowMultiple={ allowMultiple }
				closeModal={ handleCloseFileUploadModal }
				open={ fileUploadModal }
				onUpload={ handleUpload }
				upload={ uploadAsset }
			/>
		</>
	);
};

FormFileInput.propTypes = {
	align: PropTypes.string,
	allowMultiple: PropTypes.bool,
	download: PropTypes.shape( {
		title: PropTypes.string,
		uid: PropTypes.string
	} ),
	errors: PropTypes.arrayOf( PropTypes.shape( {
		error: PropTypes.bool,
		message: PropTypes.node
	} ) ),
	fieldLabel: PropTypes.node,
	fieldLabelAlignment: PropTypes.string,
	fieldLabelPosition: PropTypes.string,
	forceValidation: PropTypes.bool,
	name: PropTypes.string.isRequired,
	onBlur: PropTypes.func,
	onUpload: PropTypes.func,
	onFocus: PropTypes.func,
	required: PropTypes.bool,
	upload: PropTypes.bool,
	value: PropTypes.string,
	width: PropTypes.number
};

export default FormFileInput;
